<script setup lang="ts">
  import { MpFile } from '@borg/types';
  import { MpDivider, MpField, MpText } from '@borg/ui';

  const props = withDefaults(
    defineProps<{
      jobId: string;
      profileCvs?: string[];
      cvs?: string[];
      test?: string;
    }>(),
    {
      profileCvs: () => [],
      cvs: () => [],
      test: '',
    },
  );

  const emit = defineEmits<{
    (event: 'update:profileCvs', value: string[]): void;
    (event: 'update:cvs', value: string[]): void;
  }>();

  const { t } = useI18n();
  const projectConfig = useProjectConfig();

  const userStore = useUserStore();
  const application = jobApplicationRepository.use();

  const isProfilePublishable = ref(false);
  const isMyProfileModalVisible = ref(false);

  if (userStore.isAuthenticated) {
    try {
      const res = await userProfileService.getProfilePublishable();
      if (res.isSuccess) {
        isProfilePublishable.value = res.data.publishable;
      }
    } catch (e) {
      logger.error('Error fetching profilePublishable()');
    }
  }

  const profileCvFiles = computed<MpFile[]>(
    () =>
      application.value?.profileCvs?.map((cv) => ({
        id: cv.id,
        name: t('JOB_APPLICATION_PAGE.PROFILE_CVS_DOCUMENT_NAME', {
          project: projectConfig.title,
        }),
        createdAt: cv.updatedAt,
        size: 0,
        type: '',
        url: '',
      })) || [],
  );

  const { errorMessage, filesForUpload, onFilesForUploadSelected } = useValidateFilesBeforeUpload();

  function onDeselectCV(file: MpFile) {
    if (props.cvs.includes(file.id)) {
      emit(
        'update:cvs',
        props.cvs.filter((fId) => fId !== file.id),
      );
    }
  }

  function onToggleProfileCv(file: MpFile) {
    emit('update:cvs', []);
    emit('update:profileCvs', [file.id]);
  }

  function onToggleCv(file: MpFile) {
    emit('update:profileCvs', []);
    emit('update:cvs', [file.id]);
  }

  function onPreview() {
    isMyProfileModalVisible.value = true;
  }
</script>

<template>
  <DocumentsList
    source="cv"
    :files="profileCvFiles"
    selectable
    :select-disabled="!isProfilePublishable"
    :downloadable="false"
    :model-value="profileCvs"
    :show-subtitle="false"
    :show-context-menu="false"
    :on-preview="onPreview"
    @toggle="onToggleProfileCv"
  >
    <template #beforeTitle>
      <MpText
        class="borg_cv_tag"
        no-margin
        type="body2"
        semibold
      >
        {{
          t('JOB_APPLICATION_PAGE.PROFILE_CVS_DOCUMENT_TAG', {
            project: projectConfig.title,
          })
        }}
      </MpText>
    </template>
    <template
      v-if="!isProfilePublishable"
      #afterTitle
    >
      <MpText
        class="borg_cv_disabled"
        no-margin
        type="body2"
      >
        {{ t('MY_PROFILE_PAGE.CV_REQUIRED_DATA_LABEL') }}
      </MpText>
    </template>
  </DocumentsList>
  <template v-if="application">
    <MpDivider
      v-if="cvs.length || filesForUpload?.length || application.cvs.length"
      margin="none"
    />
    <DocumentsList
      v-model:files="application.cvs"
      :model-value="cvs"
      :files-for-upload="filesForUpload"
      source="cv"
      selectable
      @toggle="onToggleCv"
      @file-uploaded="onToggleCv"
      @file-deleted="onDeselectCV"
    />
  </template>
  <MpField :error="errorMessage">
    <UploadSurface
      :has-error="!!errorMessage"
      :accept="DEFAULT_HTML_ACCEPT_PARAMETER"
      :test="test"
      class="cv-upload-surface"
      @change="onFilesForUploadSelected"
    />
  </MpField>
  <MyProfilePreview v-model:is-open="isMyProfileModalVisible" />
</template>

<style scoped lang="scss">
  .borg_cv_tag {
    width: fit-content;
    color: var(--mp-color-secondary-default);
    padding: var(--mp-space-10) var(--mp-space-30);
    border: 1px solid var(--mp-color-secondary-default);
    border-radius: var(--mp-space-20);
  }

  .borg_cv_disabled {
    cursor: default;
  }

  .cv-upload-surface {
    margin-top: var(--mp-space-50);
  }
</style>
